import { useDispatch } from 'react-redux';
import { loadMoreSpotsTransientV2, loadSpotsTransientV2, loadSpotsMonthlyV2, loadMoreSpotsMonthlyV2, loadBulkSearchSpotsTransientV2, loadMoreBulkSearchSpotsTransientV2, loadSpotsAirportV2, } from 'store/spots/spots-actions-v2';
import { useCallback, useEffect, useRef } from 'react';
import { SEARCH_TRACKING_ACTIONS } from 'utils/search-tracking';
import { useReduxSearchState } from 'pages/search/hooks/use-redux-search-state';
import { useSortBy } from 'hooks/use-sort-by';
import { useSearchLimitValue } from 'pages/search/hooks/use-search-limit-value';
export const _arePowerBookingPeriodsEqual = (arr1, arr2) => {
    if (!arr1 || !arr2 || arr1.length !== arr2.length) {
        return false;
    }
    return arr1.every((period1, index) => {
        const period2 = arr2[index];
        return (period1.starts === period2.starts && period1.ends === period2.ends);
    });
};
export const useFetchSearchData = (airportWaitTimeForDispatching = 0) => {
    const isInitialMount = useRef(true);
    const dispatch = useDispatch();
    const { searchType, parkingType, starts, ends, powerBookingPeriods, event, vehicle, destination, } = useReduxSearchState();
    const prevStarts = useRef(starts);
    const prevEnds = useRef(ends);
    const prevPowerBookingPeriods = useRef(powerBookingPeriods);
    const prevVehicle = useRef(vehicle);
    const sortBy = useSortBy();
    const searchLimit = useSearchLimitValue();
    const loadMoreSpots = useCallback(() => {
        if (parkingType === 'power_booking') {
            const loadSpotsArgs = {
                searchTrackingAction: SEARCH_TRACKING_ACTIONS.LIST_SEARCH,
                locationContext: searchType,
            };
            dispatch(loadMoreBulkSearchSpotsTransientV2(loadSpotsArgs));
        }
        else if (parkingType === 'monthly') {
            dispatch(loadMoreSpotsMonthlyV2());
        }
        else if (parkingType === 'transient' || parkingType === 'event') {
            dispatch(loadMoreSpotsTransientV2());
        }
    }, [dispatch, parkingType, searchType]);
    const loadSpots = useCallback(({ searchTrackingAction, mapCenter = undefined, mapBounds = undefined, }) => {
        // TODO: Revert this commit by Jan 2026
        // Special case for Wrigley Field to use custom limits due to post season
        const wrigleyLimit = {
            initialResultLimit: 200,
            subsequentResultLimit: 200,
            maxDistance: 3000,
        };
        if (parkingType === 'power_booking') {
            dispatch(loadBulkSearchSpotsTransientV2({
                searchTrackingAction,
                locationContext: searchType,
                mapCenter,
                mapBounds,
                searchLimit,
            }));
        }
        else if (parkingType === 'transient') {
            dispatch(loadSpotsTransientV2({
                searchTrackingAction,
                locationContext: searchType,
                mapCenter,
                mapBounds,
                eventId: undefined,
                sortBy,
                searchLimit: destination?.id === 1 ? wrigleyLimit : searchLimit,
            }));
        }
        else if (parkingType === 'monthly') {
            dispatch(loadSpotsMonthlyV2({
                searchTrackingAction,
                locationContext: searchType,
                mapCenter,
                mapBounds,
                searchLimit,
            }));
        }
        else if (parkingType === 'event') {
            dispatch(loadSpotsTransientV2({
                searchTrackingAction,
                locationContext: searchType,
                mapCenter,
                mapBounds,
                eventId: event.id,
                sortBy,
                searchLimit: destination?.id === 1 ? wrigleyLimit : searchLimit,
            }));
        }
        else if (parkingType === 'airport') {
            dispatch(loadSpotsAirportV2({ airportWaitTimeForDispatching }));
        }
    }, [
        dispatch,
        parkingType,
        searchType,
        event,
        sortBy,
        searchLimit,
        destination?.id,
        airportWaitTimeForDispatching,
    ]);
    // load initial spots
    useEffect(() => {
        loadSpots({
            searchTrackingAction: parkingType === 'event'
                ? SEARCH_TRACKING_ACTIONS.LIST_EVENT
                : SEARCH_TRACKING_ACTIONS.LIST_SEARCH,
        });
        isInitialMount.current = false; // Set isInitialMount to false after the initial load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // detect changes and load spots
    useEffect(() => {
        if (!isInitialMount.current &&
            (prevStarts.current !== starts ||
                prevEnds.current !== ends ||
                !_arePowerBookingPeriodsEqual(prevPowerBookingPeriods.current, powerBookingPeriods) ||
                prevVehicle.current?.id !== vehicle?.id) &&
            (parkingType === 'transient' ||
                parkingType === 'event' ||
                parkingType === 'monthly' ||
                parkingType === 'power_booking' ||
                parkingType === 'airport')) {
            const hasVehicleChanged = prevVehicle.current?.id !== vehicle?.id;
            loadSpots({
                searchTrackingAction: hasVehicleChanged
                    ? SEARCH_TRACKING_ACTIONS.VEHICLE_CHANGED
                    : SEARCH_TRACKING_ACTIONS.LIST_DATE_CHANGED,
            });
        }
        // Update the previous values
        prevStarts.current = starts;
        prevEnds.current = ends;
        prevVehicle.current = vehicle;
        prevPowerBookingPeriods.current = powerBookingPeriods;
    }, [loadSpots, starts, ends, parkingType, vehicle, powerBookingPeriods]);
    return {
        loadSpots,
        loadMoreSpots,
    };
};
